// timelapseShareAnalytics — fire-and-forget instrumentation for the 絵ログ
// (timelapse) X-share funnel (growth-timelapse-share-2026-07-02.md §5.4).
//
// Two moments are measured (生成数 / 共有完了数): a successful video generation
// (`generate`) and a completed share action (`share`). The client pings the
// server with `analytics.timelapse-share { phase }`; the server records it into
// the existing `analytics_events` table as `event_type = 'share.timelapse'`.
//
// This is pure instrumentation — a failed send must NEVER surface to the user
// or block the share UX. It touches only the `Transport` port (never `WSClient`
// directly) so it is unit-testable with an in-memory fake, mirroring
// `strokeLogTailRequest` / `useCases`.

import type { TimelapseSharePhase } from '@oekaki/protocol';
import type { Transport } from './ports/Transport.js';

/**
 * Report one timelapse-share funnel moment. Fire-and-forget: silently no-ops
 * when the socket is not open (= not in the room, so the server could not
 * attribute it anyway) and swallows any send error — instrumentation must not
 * interfere with the share flow.
 */
export const reportTimelapseShare = (transport: Transport, phase: TimelapseSharePhase): void => {
  // The event is attributed server-side from the connection's room; if the
  // socket is not open there is nothing to attribute, so drop it quietly.
  if (transport.status.t !== 'open') return;
  try {
    transport.send({ t: 'analytics.timelapse-share', phase });
  } catch {
    // send-before-open / serialisation quirk — analytics is best-effort.
  }
};

/**
 * Outcome of a `navigator.share({ files })` promise settlement, used to decide
 * the share-funnel + fallback behaviour:
 *
 * - `shared`  — resolved: the OS handed the video to a share target → count as
 *               共有完了 (`share`).
 * - `aborted` — rejected with `AbortError`: the user dismissed the share sheet.
 *               §5.3.4 黙殺方針 — NOT counted, NOT a fallback (they simply
 *               changed their mind).
 * - `failed`  — rejected with anything else (e.g. the target refused the file):
 *               NOT counted here; the UI falls back to the PC download+intent
 *               path where the share is counted instead.
 */
export type WebShareSettlement = 'shared' | 'aborted' | 'failed';

/** Classify a `navigator.share` settlement. `null` = resolved (shared). */
export const classifyWebShareSettlement = (error: unknown): WebShareSettlement => {
  if (error === null || error === undefined) return 'shared';
  if (error instanceof Error && error.name === 'AbortError') return 'aborted';
  return 'failed';
};
